import { Zone, ZoneBinPattern } from "./schema/warehouse-grid";

export const MAX_ZONE_BIN_PATTERN_LENGTH = 100;
export const MAX_ZONE_BIN_PATTERN_QUANTIFIERS = 12;
export const MAX_ZONE_BIN_MATCH_LENGTH = 64;

export interface ZoneBinPatternError {
  zoneId: string;
  patternIndex: number | null;
  message: string;
}

export interface CompiledZoneBinPattern {
  zoneId: string;
  patternIndex: number;
  pattern: string;
  regex: RegExp;
  specificity: number;
  zoneOrder: number;
}

export interface ZoneBinPatternMatch {
  zoneId: string;
  patternIndex: number;
  pattern: string;
}

type ZonesWithPatterns = Record<
  string,
  Pick<Zone, "isDefault" | "binPatterns">
>;

const ESCAPED_CLASS_CHARS = new Set([
  "d",
  "D",
  "w",
  "W",
  "s",
  "S",
  "b",
  "B",
  "n",
  "t",
  "r",
  "f",
  "v",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "p",
  "P",
  "k",
  "x",
  "u",
  "c",
]);

const OPERATOR_CHARS = new Set(["^", "$", ".", "*", "+", "?", "(", ")", "|"]);

export function patternSpecificity(pattern: string): number {
  let score = 0;
  let i = 0;
  while (i < pattern.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ch = pattern[i]!;
    if (ch === "\\") {
      const next = pattern[i + 1];
      if (next !== undefined && !ESCAPED_CLASS_CHARS.has(next)) {
        score += 1;
      }
      i += 2;
      continue;
    }
    if (ch === "[") {
      i += 1;
      if (pattern[i] === "^") {
        i += 1;
      }
      if (pattern[i] === "]") {
        i += 1;
      }
      while (i < pattern.length && pattern[i] !== "]") {
        if (pattern[i] === "\\") {
          i += 1;
        }
        i += 1;
      }
      i += 1;
      continue;
    }
    if (ch === "{") {
      const close = pattern.indexOf("}", i);
      if (
        close !== -1 &&
        /^\{\d+(,\d*)?\}$/.test(pattern.slice(i, close + 1))
      ) {
        i = close + 1;
        continue;
      }
    }
    if (ch === "(" && pattern[i + 1] === "?") {
      if (
        pattern[i + 2] === "<" &&
        pattern[i + 3] !== "=" &&
        pattern[i + 3] !== "!"
      ) {
        const close = pattern.indexOf(">", i);
        i = close === -1 ? pattern.length : close + 1;
      } else {
        i += 3;
      }
      continue;
    }
    if (OPERATOR_CHARS.has(ch)) {
      i += 1;
      continue;
    }
    score += 1;
    i += 1;
  }
  return score;
}

function compileZoneBinPattern(pattern: string): RegExp | null {
  if (zoneBinPatternProblem(pattern) !== null) {
    return null;
  }
  return new RegExp(pattern, "i");
}

export function unsafeZoneBinPatternProblem(pattern: string): string | null {
  let quantifiers = 0;
  let i = 0;
  while (i < pattern.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ch = pattern[i]!;
    if (ch === "\\") {
      const next = pattern[i + 1];
      if (next !== undefined && /[1-9]/.test(next)) {
        return "Backreferences are not supported";
      }
      if (next === "k") {
        return "Backreferences are not supported";
      }
      i += 2;
      continue;
    }
    if (ch === "[") {
      i += 1;
      if (pattern[i] === "^") {
        i += 1;
      }
      if (pattern[i] === "]") {
        i += 1;
      }
      while (i < pattern.length && pattern[i] !== "]") {
        if (pattern[i] === "\\") {
          i += 1;
        }
        i += 1;
      }
      i += 1;
      continue;
    }
    if (ch === "(" && pattern[i + 1] === "?") {
      const tail = pattern.slice(i + 2, i + 4);
      if (
        tail.startsWith("=") ||
        tail.startsWith("!") ||
        tail === "<=" ||
        tail === "<!"
      ) {
        return "Lookahead and lookbehind are not supported";
      }
    }
    if (ch === ")") {
      const next = pattern[i + 1];
      if (next === "*" || next === "+" || next === "{") {
        return "Repeating a group is not supported";
      }
    }
    if (ch === "*" || ch === "+" || ch === "?" || ch === "{") {
      quantifiers += 1;
      if (quantifiers > MAX_ZONE_BIN_PATTERN_QUANTIFIERS) {
        return `Pattern uses more than ${MAX_ZONE_BIN_PATTERN_QUANTIFIERS} repetitions`;
      }
    }
    i += 1;
  }
  return null;
}

export function validateZoneBinPatterns(
  zones: ZonesWithPatterns,
): ZoneBinPatternError[] {
  const errors: ZoneBinPatternError[] = [];
  for (const [zoneId, zone] of Object.entries(zones)) {
    const patterns = zone.binPatterns ?? [];
    if (patterns.length === 0) {
      continue;
    }
    if (zone.isDefault === true) {
      errors.push({
        zoneId,
        patternIndex: null,
        message:
          "The default zone already receives every unmatched bin and cannot have patterns",
      });
      continue;
    }
    for (const [patternIndex, entry] of patterns.entries()) {
      const message = zoneBinPatternProblem(entry.pattern);
      if (message !== null) {
        errors.push({ zoneId, patternIndex, message });
      }
    }
  }
  return errors;
}

export function zoneBinPatternProblem(pattern: string): string | null {
  if (pattern.trim().length === 0) {
    return "Pattern is empty";
  }
  if (pattern.length > MAX_ZONE_BIN_PATTERN_LENGTH) {
    return `Pattern is longer than ${MAX_ZONE_BIN_PATTERN_LENGTH} characters`;
  }
  try {
    new RegExp(pattern, "i");
  } catch (error) {
    return error instanceof Error
      ? error.message
      : "Pattern is not a valid regular expression";
  }
  return unsafeZoneBinPatternProblem(pattern);
}

export function compileZoneBinPatterns(
  zones: ZonesWithPatterns,
  zoneSequence: readonly string[] | null | undefined,
): CompiledZoneBinPattern[] {
  const compiled: CompiledZoneBinPattern[] = [];
  const sequence = zoneSequence ?? [];
  for (const [zoneId, zone] of Object.entries(zones)) {
    if (zone.isDefault === true) {
      continue;
    }
    const sequenceIndex = sequence.indexOf(zoneId);
    const zoneOrder = sequenceIndex === -1 ? sequence.length : sequenceIndex;
    const entries: ZoneBinPattern[] = zone.binPatterns ?? [];
    for (const [patternIndex, entry] of entries.entries()) {
      if (!entry.enabled) {
        continue;
      }
      const regex = compileZoneBinPattern(entry.pattern);
      if (!regex) {
        continue;
      }
      compiled.push({
        zoneId,
        patternIndex,
        pattern: entry.pattern,
        regex,
        specificity: patternSpecificity(entry.pattern),
        zoneOrder,
      });
    }
  }
  compiled.sort(
    (a, b) =>
      b.specificity - a.specificity ||
      a.zoneOrder - b.zoneOrder ||
      a.patternIndex - b.patternIndex,
  );
  return compiled;
}

export function matchZoneBinPattern(
  candidates: readonly string[],
  compiled: readonly CompiledZoneBinPattern[],
): ZoneBinPatternMatch | null {
  const bounded = candidates.map((candidate) =>
    candidate.slice(0, MAX_ZONE_BIN_MATCH_LENGTH),
  );
  for (const entry of compiled) {
    for (const candidate of bounded) {
      if (entry.regex.test(candidate)) {
        return {
          zoneId: entry.zoneId,
          patternIndex: entry.patternIndex,
          pattern: entry.pattern,
        };
      }
    }
  }
  return null;
}

const compiledCache = new WeakMap<
  object,
  {
    sequence: readonly string[] | null | undefined;
    compiled: CompiledZoneBinPattern[];
  }
>();

export function getCompiledZoneBinPatterns(grid: {
  zones?: ZonesWithPatterns | null;
  zoneSequence?: readonly string[] | null;
}): CompiledZoneBinPattern[] {
  const zones = grid.zones;
  if (!zones) {
    return [];
  }
  const cached = compiledCache.get(zones);
  if (cached && cached.sequence === grid.zoneSequence) {
    return cached.compiled;
  }
  const compiled = compileZoneBinPatterns(zones, grid.zoneSequence);
  compiledCache.set(zones, { sequence: grid.zoneSequence, compiled });
  return compiled;
}
